import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const dpRoot = 'Catalogs|Achievements|Logo|';

const sharedData: Pick<LocDef, 'htmlAllowed' | 'shared' | 'portal' | 'feature'> = {
	htmlAllowed: false,
	shared: false,
	portal: 'SP',
	feature: HubFeature.CredentialAchievementsLogoDownload,
};

export type SPAchievementLogoLocs =
	| 'SPAchievementLogoTabTitle'
	| 'SPAchievementLogoHeading'
	| 'SPAchievementLogoUpdateError'
	| 'SPAchievementLogoSectionTitle'
	| 'SPAchievementLogoEdit'
	| 'SPAchievementLogoUpdate'
	| 'SPAchievementLogoCancel'
	| 'SPAchievementLogoFileType'
	| 'SPAchievementLogoFileSize'
	| 'SPAchievementLogoFileHint'
	| 'SPAchievementLogoZippedLogo'
	| 'SPAchievementLogoChangesSaved'
	| 'SPAchievementLogoEnabled'
	| 'SPAchievementLogoEnabledHint';

export const spAchievementLogoLocs: LocDefs<SPAchievementLogoLocs> = {
	SPAchievementLogoTabTitle: {
		defaultValue: 'Achievements Logo',
		description: 'Page section title.',
		legacyName: 'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Title.Tab',
		displayPath: `${dpRoot}Tab Title`,
		displayType: 'Title',
		...sharedData,
	},
	SPAchievementLogoHeading: {
		defaultValue: 'Achievements Catalog',
		description: 'Page heading for the achievements logo page.',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Title.PageHeading',
		displayPath: `${dpRoot}Heading`,
		displayType: 'Title',
		...sharedData,
	},
	SPAchievementLogoUpdateError: {
		defaultValue: 'Achievements Catalog',
		description: 'Message shown if there was an error updating the logo..',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Notification.UpdateError',
		displayPath: `${dpRoot}Update Error`,
		displayType: 'Notification',
		...sharedData,
	},
	SPAchievementLogoSectionTitle: {
		defaultValue: 'Logo File',
		description: 'Message shown if there was an error updating the logo.',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Title.SectionTitle',
		displayPath: `${dpRoot}Section Title`,
		displayType: 'Title',
		...sharedData,
	},
	SPAchievementLogoEdit: {
		defaultValue: 'Edit',
		description: 'Edit button for achievement logos',
		legacyName: 'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Button.Edit',
		displayPath: `${dpRoot}Edit`,
		displayType: 'Button',
		...sharedData,
	},
	SPAchievementLogoUpdate: {
		defaultValue: 'Update',
		description: 'Update button for achievement logos',
		legacyName: 'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Button.Update',
		displayPath: `${dpRoot}Update`,
		displayType: 'Button',
		...sharedData,
	},
	SPAchievementLogoCancel: {
		defaultValue: 'Cancel',
		description: 'Button to cancel editing achievement logos',
		legacyName: 'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Button.Cancel',
		displayPath: `${dpRoot}Cancel`,
		displayType: 'Button',
		...sharedData,
	},
	SPAchievementLogoFileType: {
		defaultValue: 'The file must be a ZIP format',
		description: 'Error for the file input field with information about file type restrictions',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldError.FileType',
		displayPath: `${dpRoot}File Type Error`,
		displayType: 'Error',
		...sharedData,
	},
	SPAchievementLogoFileSize: {
		defaultValue: 'The file must not exceed 20 MB',
		description: 'Error for the file input field with information about file size restrictions',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldError.FileSize',
		displayPath: `${dpRoot}File Size Error`,
		displayType: 'Error',
		...sharedData,
	},
	SPAchievementLogoFileHint: {
		defaultValue: 'The file must be a ZIP format and not exceed 20 MB',
		description: 'Hint for the file size and file type restrictions for input',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldError.FileHint',
		displayPath: `${dpRoot}File Hint`,
		displayType: 'Content',
		...sharedData,
	},
	SPAchievementLogoZippedLogo: {
		defaultValue: 'Zipped Logo File',
		description: 'Field label for the input to enter a zipped logo file',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldLabel.ZippedLogoFile',
		displayPath: `${dpRoot}Zipped Logo File`,
		displayType: 'Label',
		...sharedData,
	},
	SPAchievementLogoChangesSaved: {
		defaultValue: 'Changes saved',
		description: 'Notification message that achievement logo changes were successfully saved.',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.Notification.ChangesSaved',
		displayPath: `${dpRoot}Changes Saved`,
		displayType: 'Notification',
		...sharedData,
	},
	SPAchievementLogoEnabled: {
		defaultValue: 'Logo Download Enabled',
		description: 'Field label for logo download enabled checkbox',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldLabel.Enabled',
		displayPath: `${dpRoot}Logo Enabled`,
		displayType: 'Label',
		...sharedData,
	},
	SPAchievementLogoEnabledHint: {
		defaultValue: 'A file must be attached or uploaded before editing',
		description: 'Hint for the checkbox to enable logo download',
		legacyName:
			'Credential.Achievements.LogoDownload/CatalogsAchievement.Logo.FieldHint.Enabled',
		displayPath: `${dpRoot}Logo Enabled`,
		displayType: 'Label',
		...sharedData,
	},
};

export type SPAchievementLogoLocKeys = LocMap<SPAchievementLogoLocs>;

export const spAchievementLogoLocKeys: SPAchievementLogoLocKeys =
	MapLocKeys<SPAchievementLogoLocs>(spAchievementLogoLocs);
